
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import {
  GET_COMPANY_DETAILS,
  GET_USER_DETAILS
} from "@/store/modules/auth/constants";
import {
  APP_ASSESSMENT_RESULT,
  APP_ASSESSMENTS
} from "@/store/modules/common/constants";
import {
  REDO_ASSESSMENT_REQUEST,
  FETCH_APPLICANT_ASSESSMENTS,
  APPLICANT_ASSESSMENTS
} from "@/store/modules/recruiter/constants";
import { resolveEnglishAssessmentTypeByPersona } from "@/utils/assessment_persona";
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";

export type AssessmentCardType = {
  status?: string | null;
  is_show?: boolean;
  type?: string;
  title?: string;
  description?: string;
  icon?: string;
  version?: string;
  availability_status?: "AVAILABLE" | "UPCOMING" | "EXPIRED";
  start_at?: string | null;
  expires_at?: string | null;
};

type AssessmentStatusSource = {
  status?: string;
  result?: {
    status?: string;
  };
  results?: Array<{
    status?: string;
  }>;
};
export default Vue.extend({
  name: "DashboardAssessmentCard",
  props: {
    assessment: {
      type: Object as () => AssessmentCardType,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      permissionDialog: false,
      permissionLoading: false,
      permissionMessage: "",
      currentHost: ""
    };
  },
  computed: {
    ...mapGetters("common", {
      app_assessments: APP_ASSESSMENTS
    }),
    ...mapGetters("auth", {
      get_user_details: GET_USER_DETAILS
    }),
    ...mapGetters("auth", {
      get_company_details: GET_COMPANY_DETAILS
    }),
    ...mapGetters("recruiter", {
      applicantAssessments: APPLICANT_ASSESSMENTS
    }),
    // Assignment-driven tenants (no company assessment_setting) have no approval gate.
    is_assignment_driven(): boolean {
      return !this.get_company_details?.storage_path?.assessment_setting
        ?.candidates_english;
    },
    // Scheduling window: takeable only when AVAILABLE (or no window set).
    is_within_window(): boolean {
      const status = this.assessment.availability_status;
      return !status || status === "AVAILABLE";
    },
    availability_label(): string {
      if (this.assessment.availability_status === "EXPIRED") {
        return this.$t("assessments.expired") as string;
      }
      if (
        this.assessment.availability_status === "UPCOMING" &&
        this.assessment.start_at
      ) {
        const when = new Date(this.assessment.start_at).toLocaleString(
          undefined,
          {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit"
          }
        );
        return `${this.$t("assessments.available-from")} ${when}`;
      }
      return this.$t("assessments.attempted") as string;
    }
  },
  methods: {
    ...mapActions("recruiter", {
      redo_assessment_request: REDO_ASSESSMENT_REQUEST,
      fetchApplicantAssessments: FETCH_APPLICANT_ASSESSMENTS
    }),
    ...mapActions("common", {
      app_assessment_result: APP_ASSESSMENT_RESULT
    }),
    async do_assessment() {
      this.loading = true;

      // Additional assessments: no permission request here; go to list. Permissions are requested when candidate selects a specific assessment.
      if (this.$props.assessment.type === "ai") {
        this.$router.push("/candidate/ai-assessments");
        this.loading = false;
        return;
      }

      this.permissionMessage = "";

      // Check available devices
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some((d) => d.kind === "videoinput");
        const hasMic = devices.some((d) => d.kind === "audioinput");

        if (!hasCamera && !hasMic) {
          this.permissionMessage = this.$t(
            "assessments.permission-allow-both"
          ) as string;
        } else if (!hasCamera) {
          this.permissionMessage = this.$t(
            "assessments.permission-allow-camera"
          ) as string;
        } else if (!hasMic) {
          this.permissionMessage = this.$t(
            "assessments.permission-allow-mic"
          ) as string;
        } else {
          // Both exist, so ask for permission
          this.permissionMessage = this.$t(
            "assessments.permission-allow-both"
          ) as string;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error checking devices:", e);
        this.permissionMessage = this.$t(
          "assessments.permission-allow-both"
        ) as string;
      }

      this.permissionDialog = true;

      // Attempt to show native permission prompt immediately if possible
      try {
        if (
          navigator &&
          navigator.mediaDevices &&
          navigator.mediaDevices.getUserMedia
        ) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });
          if (stream && stream.getTracks) {
            stream.getTracks().forEach((t) => t.stop());

            // If permission is successfully granted (e.g. user already allowed or clicked Allow quickly),
            // automatically proceed without waiting for user to click anything manually.
            this.handlePermissionContinue();
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error("Native permission check failed:", e);
        // If specific permission error, update message
        if (
          e.name === "NotAllowedError" ||
          e.name === "PermissionDeniedError"
        ) {
          // Keep the generic "Please allow..." message or refine it if you want
        } else if (
          e.name === "NotFoundError" ||
          e.name === "DevicesNotFoundError"
        ) {
          // This might catch cases where devices are missing if enumerateDevices didn't catch it
          this.permissionMessage = this.$t(
            "assessments.permission-allow-both"
          ) as string;
        }
      }
    },
    async handlePermissionContinue() {
      this.permissionLoading = true;

      // Proceed with navigation logic
      if (this.$props.assessment.type === "ai") {
        this.$router.push("/candidate/ai-assessments");
        this.loading = false;
        this.permissionLoading = false;
        this.permissionDialog = false;
        return;
      }
      if (this.$props.assessment.status === "CANCELLED") {
        const assessment = this.app_assessments.find(
          (val: AppAssessments.Assessment) =>
            val.assessment_type === this.$props.assessment.version
        );
        await this.redo_assessment_request(assessment.id);
        this.$emit("update:assessment_status", "REDO");
        this.$emit("update:is_show_assessment", false);
      } else {
        const navigation_map: Record<string, string> = {
          english: "/candidate/assessments/english",
          annotation: "/candidate/assessments/annotation"
        };
        this.$router.push(
          navigation_map[this.$props.assessment.type as string]
        );
      }
      this.loading = false;
      this.permissionLoading = false;
      this.permissionDialog = false;
    },
    async get_assessment() {
      if (this.$props.assessment.type === "ai") {
        const applicantId = this.get_user_details?.id;
        if (!applicantId) return;

        await this.fetchApplicantAssessments({
          applicant_id: applicantId,
          params: { page: 1, limit: 100 } // Fetch all to check status
        });

        const assessments = this.applicantAssessments?.data || [];
        if (assessments.length > 0) {
          const allCompleted = assessments.every((a: AssessmentStatusSource) =>
            this.isCompleted(a)
          );
          // Only show "Submitted" on the card when ALL additional assessments are completed.
          // Per-assessment status (Submitted, Admin Approval Required) is shown inside the list.
          if (allCompleted) {
            this.$emit("update:assessment_status", "COMPLETED");
            this.$emit("update:is_show_assessment", false);
          } else {
            // Only show "Resume" when at least one assessment is actually in progress (user started but not completed).
            // Assigned-but-never-started should show "Start Assessment", not "Resume Assessment".
            const hasAnyInProgress = assessments.some(
              (a: AssessmentStatusSource) =>
                this.extractStatus(a) === "IN_PROGRESS"
            );
            this.$emit(
              "update:assessment_status",
              hasAnyInProgress ? "IN_PROGRESS" : "NOT_STARTED"
            );
            this.$emit("update:is_show_assessment", true);
          }
        }
        return;
      }
      let eng_version = resolveEnglishAssessmentTypeByPersona(
        this.get_user_details?.persona,
        this.get_company_details?.storage_path?.assessment_setting
          ?.candidates_english
      );
      let ann_version =
        this.get_company_details?.storage_path?.assessment_setting
          ?.candidates_annotation;

      const findVersion =
        this.$props.assessment.type === "english"
          ? this.$props.assessment.version || eng_version
          : ann_version;

      // Try to find assessment by version first
      let assessment = this.app_assessments.find(
        (val: AppAssessments.Assessment) => val.assessment_type === findVersion
      );

      // Fallback: Try to find by assessment name or type containing the assessment type
      if (!assessment) {
        const searchType = this.$props.assessment.type?.toLowerCase();
        assessment = this.app_assessments.find(
          (val: AppAssessments.Assessment) =>
            val.assessment_name?.toLowerCase().includes(searchType) ||
            val.assessment_type?.toLowerCase().includes(searchType)
        );
      }

      // Fallback: Use the first assessment if none found
      if (!assessment && this.app_assessments.length > 0) {
        assessment = this.app_assessments[0];
      }

      // Check if english/annotation assessment is attempted or not
      if (assessment) {
        const assessmentResultExist = await this.app_assessment_result({
          assessment_id: assessment.id
        });
        if (!assessmentResultExist) {
          // Fresh candidate - never attempted; show "Take" not "Resume"
          this.$emit("update:assessment_status", "NOT_STARTED");
          this.$emit("update:is_show_assessment", true);
        } else if (assessmentResultExist) {
          type AssessmentResultMeta = {
            status?: AppAssessments.AppAssessmentStatus | string | null;
            result?: {
              status?: AppAssessments.AppAssessmentStatus | string | null;
              overall_score?: number | null;
            } | null;
            resume_admin_status?: "PENDING" | "APPROVED" | "REJECTED" | null;
            overall_score?: number | null;
          };

          const typedResult = assessmentResultExist as AssessmentResultMeta;
          let rawStatus = typedResult.status || typedResult.result?.status;
          const adminStatus = typedResult.resume_admin_status ?? null;

          // If user just submitted (redirect from completion), show "Submitted" even when API still returns IN_PROGRESS.
          let forceSubmitted = false;
          const JUST_SUBMITTED_TTL_MS = 2 * 60 * 1000; // 2 minutes
          const now = typeof Date.now === "function" ? Date.now() : 0;
          if (
            typeof sessionStorage !== "undefined" &&
            this.$props.assessment.type === "english"
          ) {
            const englishFlag = sessionStorage.getItem(
              "english_assessment_just_submitted"
            );
            const englishTs = sessionStorage.getItem(
              "english_assessment_just_submitted_ts"
            );
            const englishJustSubmitted =
              englishFlag === "1" ||
              (englishTs &&
                now - parseInt(englishTs, 10) < JUST_SUBMITTED_TTL_MS);
            if (
              englishJustSubmitted &&
              rawStatus === AppAssessments.AppAssessmentStatus.IN_PROGRESS
            ) {
              sessionStorage.removeItem("english_assessment_just_submitted");
              sessionStorage.removeItem("english_assessment_just_submitted_ts");
              rawStatus = AppAssessments.AppAssessmentStatus.RESULT_PROCESSING;
              forceSubmitted = true;
            }
          }

          // RESULT_PROCESSING or COMPLETED = always show "Submitted" (even before score is computed).
          const isSubmitted =
            rawStatus ===
              AppAssessments.AppAssessmentStatus.RESULT_PROCESSING ||
            rawStatus === AppAssessments.AppAssessmentStatus.COMPLETED;
          // IN_PROGRESS: if admin approved show "Resume", otherwise "Wait for approval"
          const isInProgress =
            rawStatus === AppAssessments.AppAssessmentStatus.IN_PROGRESS;
          // Assignment-driven tenants have no admin-approval gate — always resumable.
          const canResume =
            isInProgress &&
            (this.is_assignment_driven || adminStatus === "APPROVED");
          const waitForApproval =
            isInProgress &&
            !this.is_assignment_driven &&
            adminStatus !== "APPROVED";
          const showRedo =
            rawStatus === AppAssessments.AppAssessmentStatus.CANCELLED;

          if (forceSubmitted || isSubmitted) {
            this.$emit("update:is_show_assessment", false);
            this.$emit(
              "update:assessment_status",
              forceSubmitted
                ? AppAssessments.AppAssessmentStatus.RESULT_PROCESSING
                : rawStatus
            );
          } else if (canResume || showRedo) {
            this.$emit("update:is_show_assessment", true);
            this.$emit(
              "update:assessment_status",
              canResume
                ? AppAssessments.AppAssessmentStatus.IN_PROGRESS
                : rawStatus
            );
          } else {
            this.$emit("update:is_show_assessment", false);
            this.$emit(
              "update:assessment_status",
              waitForApproval
                ? AppAssessments.AppAssessmentStatus.IN_PROGRESS
                : rawStatus
            );
          }
        }
      }
    },
    isCompleted(assessment: AssessmentStatusSource): boolean {
      const status = this.extractStatus(assessment);
      return status === "COMPLETED" || status === "RESULT_PROCESSING";
    },
    extractStatus(assessment: AssessmentStatusSource): string | undefined {
      const directStatus = assessment?.status;
      const resultStatus = assessment?.result?.status;
      const firstResultStatus = Array.isArray(assessment?.results)
        ? assessment.results[0]?.status
        : undefined;
      return directStatus || resultStatus || firstResultStatus;
    }
  },
  async mounted() {
    this.currentHost = window.location.host;
    this.loading = true;
    await this.get_assessment();
    this.loading = false;
  }
});
